import { App, watch } from 'vue';
import type { Router } from 'vue-router';
import { useTenantStore } from './stores/useTenantStore';
import { storeToRefs } from 'pinia';

export async function initSentry(app: App, router: Router) {
  const VITE_ENV = import.meta.env.VITE_ENV?.toUpperCase() || 'DEVELOPMENT';
  if (VITE_ENV === 'DEVELOPMENT') {
    return;
  } else {
    console.info('Patient Portal version:', import.meta.env.RELEASE_NUMBER || 'unknown');
    if (VITE_ENV !== 'PRODUCTION' && VITE_ENV !== 'PROD') console.info('Sentry init', VITE_ENV);
  }

  if (!import.meta.env.VITE_PATIENT_PORTAL_SENTRY_DSN) {
    if (VITE_ENV !== 'PRODUCTION' && VITE_ENV !== 'PROD') console.info('Sentry no DSN');
    return;
  }

  const tenantStore = useTenantStore();
  const { tenant } = storeToRefs(tenantStore);
  try {
    const Sentry = await import('@sentry/vue');

    Sentry.init({
      app,
      dsn: import.meta.env.VITE_PATIENT_PORTAL_SENTRY_DSN,
      release: import.meta.env.RELEASE_NUMBER,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          // Block sensitive data from being sent to Sentry
          mask: ['[data-private]', '.phi', '.pii'],
          block: ['[data-block]', '.replay-block'],
        }),
        Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      ],
      environment: VITE_ENV,
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0.2,
      replaysOnErrorSampleRate: 1,
      //
      enableLogs: true,
    });

    watch(
      () => tenant.value?.tenantId,
      (tenantId) => {
        Sentry.setTag('tenant', tenantId || 'unknown');
      },
      { immediate: true },
    );
  } catch (err) {
    console.warn('Sentry init failed', err);
  }
}
